import { useCallback, useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import BookingFormAuditSessionTable from "../../components/booking/BookingFormAuditSessionTable";
import type { BookingFormAuditStats } from "../../components/booking/BookingFormAuditAnalytics";
import type { AuditSession } from "../../utils/bookingFormAuditSession";
import {
  buildVehicleNameLookup,
  DEFAULT_SESSION_FILTERS,
  type SessionClientFilters,
} from "../../utils/bookingFormAuditSession";
import {
  SESSION_PAGE_SIZE,
  useBookingFormAuditQuery,
} from "../../hooks/useBookingFormAuditQuery";

export default function BookingFormAuditLogs() {
  const location = useLocation();
  const {
    bookingSource,
    setBookingSource,
    customerEmail,
    setCustomerEmail,
    sessionId,
    setSessionId,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    buildQuery,
    sourceOptions,
  } = useBookingFormAuditQuery();

  const [sessions, setSessions] = useState<AuditSession[]>([]);
  const [sessionTotal, setSessionTotal] = useState(0);
  const [stats, setStats] = useState<BookingFormAuditStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState("");
  const [vehicleNames, setVehicleNames] = useState<Record<string, string>>({});
  const [clientFilters, setClientFilters] =
    useState<SessionClientFilters>(DEFAULT_SESSION_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(SESSION_PAGE_SIZE);

  useEffect(() => {
    const incoming = (
      location.state as { clientFilters?: SessionClientFilters } | null
    )?.clientFilters;
    if (!incoming) return;
    setClientFilters({ ...DEFAULT_SESSION_FILTERS, ...incoming });
    setCurrentPage(1);
    requestAnimationFrame(() => {
      document
        .getElementById("audit-session-list")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [location.state]);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const offset = (currentPage - 1) * pageSize;
      const pageQuery = buildQuery({
        limit: pageSize,
        offset,
        clientFilters,
      });
      const response = await axiosInstance.get(
        `/booking-form-audit/sessions?${pageQuery}`,
      );
      const payload = response.data;
      const pageSessions: AuditSession[] = Array.isArray(payload)
        ? payload
        : (payload?.sessions ?? []);
      const total = Array.isArray(payload)
        ? pageSessions.length
        : Number(payload?.total ?? pageSessions.length);

      setSessions(pageSessions);
      setSessionTotal(total);

      const totalPages = Math.max(1, Math.ceil(total / pageSize));
      if (currentPage > totalPages) {
        setCurrentPage(totalPages);
      }
    } catch (err) {
      setError("Failed to load booking form audit logs");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [buildQuery, clientFilters, currentPage, pageSize]);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const query = buildQuery({
        includeSessionLimit: false,
        clientFilters,
      });
      const response = await axiosInstance.get(
        `/booking-form-audit/stats${query ? `?${query}` : ""}`,
      );
      setStats({
        ...response.data,
        trafficSources: response.data.trafficSources ?? [],
        topUtmTerms: response.data.topUtmTerms ?? [],
        topLandingPages: response.data.topLandingPages ?? [],
        avgEngagementSeconds: response.data.avgEngagementSeconds ?? null,
        avgScrollDepth: response.data.avgScrollDepth ?? null,
      });
    } catch (err) {
      console.error(err);
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, [buildQuery, clientFilters]);

  const refreshAll = useCallback(() => {
    void fetchSessions();
    void fetchStats();
  }, [fetchSessions, fetchStats]);

  useEffect(() => {
    const timer = setTimeout(() => {
      void fetchSessions();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchSessions]);

  useEffect(() => {
    const timer = setTimeout(() => {
      void fetchStats();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchStats]);

  useEffect(() => {
    void axiosInstance
      .get("/vehicletypes")
      .then((response) => {
        setVehicleNames(buildVehicleNameLookup(response.data ?? []));
      })
      .catch((err) => {
        console.error(err);
      });
  }, []);

  const handleClientFiltersChange = useCallback(
    (filters: SessionClientFilters) => {
      setClientFilters(filters);
      setCurrentPage(1);
    },
    [],
  );

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const handleServerFiltersChange = useCallback(() => {
    setCurrentPage(1);
  }, []);

  return (
    <div>
      <div className="audit-no-print">
        <PageBreadcrumb pageTitle="Website Audit" />
        <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
          Session-level audit of website exploration and the booking form — pages viewed,
          clicks, navigation, and funnel progress. Charts are on{" "}
          <Link to="/booking-analytics" className="font-medium text-brand-600 hover:underline">
            Booking Analytics
          </Link>
          .
        </p>
      </div>

      <BookingFormAuditSessionTable
        sessions={sessions}
        sessionTotal={sessionTotal}
        stats={stats}
        loading={loading || statsLoading}
        error={error}
        sourceOptions={sourceOptions}
        bookingSource={bookingSource}
        fromDate={fromDate}
        toDate={toDate}
        customerEmail={customerEmail}
        sessionId={sessionId}
        onBookingSourceChange={(value) => {
          setBookingSource(value);
          handleServerFiltersChange();
        }}
        onFromDateChange={(value) => {
          setFromDate(value);
          handleServerFiltersChange();
        }}
        onToDateChange={(value) => {
          setToDate(value);
          handleServerFiltersChange();
        }}
        onCustomerEmailChange={(value) => {
          setCustomerEmail(value);
          handleServerFiltersChange();
        }}
        onSessionIdChange={(value) => {
          setSessionId(value);
          handleServerFiltersChange();
        }}
        onRefresh={refreshAll}
        vehicleNames={vehicleNames}
        clientFilters={clientFilters}
        onClientFiltersChange={handleClientFiltersChange}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  );
}
